import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import type { DriverLocation } from '@breaker/shared-types';
import { light } from '@breaker/design-tokens';
import { formatRelative, formatTime, shortId } from '@/lib/format';
import { config } from '@/config';

/**
 * Vanilla-Leaflet map of driver location points. Imperative lifecycle
 * (no react-leaflet) to sidestep the React-19 peer dance. Uses circleMarker
 * only — no default marker icon, so the bundler icon-404 footgun never fires.
 *
 * Two modes:
 *  - 'trail' (default): points are one driver's chronological path (earliest
 *    first). The LAST point is the newest fix and is emphasised; older points
 *    are faint and joined by a trail polyline (drawn only when 2+ points exist).
 *  - 'markers': points are independent current positions (e.g. latest-per-driver
 *    across a fleet). Every point is emphasised equally; no trail line is drawn.
 */

// Colour law: the emphasised live fix is BRAND emphasis, not danger — it
// takes identity (the old #e4002b brand red predates red's demotion to
// status-only). All map paint reads the canon package, never a hex.
const LATEST_COLOR = light.identity; // emphasised fix

// Popup body for a driver fix. recordedAt drives a relative "age" (the thing a
// dispatcher cares about most — how stale is this?) plus the absolute clock time
// and GPS accuracy when the phone reported it. escapeHtml guards the injected
// strings even though they're formatter output (defence in depth for innerHTML).
function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : c === '"' ? '&quot;' : '&#39;',
  );
}

function popupHtml(p: DriverLocation, names?: Map<string, string>): string {
  const nameLine = names
    ? `<div style="font-weight:700;font-size:14px;margin-bottom:2px">${escapeHtml(
        names.get(p.userId) ?? shortId(p.userId),
      )}</div>`
    : '';
  const age = escapeHtml(formatRelative(p.recordedAt));
  const clock = escapeHtml(formatTime(p.recordedAt));
  const acc =
    p.accuracyM != null
      ? `<div style="color:${light.mute};font-size:11px;margin-top:2px">±${Math.round(p.accuracyM)} m</div>`
      : '';
  return (
    `<div style="font-size:13px;line-height:1.3">` +
    nameLine +
    `<div style="font-weight:600">${age}</div>` +
    `<div style="color:${light.mute};font-size:12px">${clock}</div>` +
    acc +
    `</div>`
  );
}
const TRAIL_COLOR = light.mute; // older trail points
const STOP_COLOR = light.ink; // stop pins, distinct from the driver fix

/**
 * A stop pin. House map language (mirrors the portal's load-map): solid pin
 * for a collection (origin), hollow pin for a delivery (destination).
 */
export interface MapStopPin {
  lat: number;
  lng: number;
  type: 'pickup' | 'dropoff';
  label: string;
}

interface LocationMapProps {
  points: DriverLocation[];
  /** 'trail' = one driver's path (default); 'markers' = independent current positions. */
  mode?: 'trail' | 'markers';
  className?: string;
  /** userId → displayName; when present, popups show a bold name line. */
  names?: Map<string, string>;
  /** Load stops to pin (collection solid, delivery hollow). Drawn even with zero driver points. */
  stops?: MapStopPin[];
}

export function LocationMap({ points, mode = 'trail', className, names, stops }: LocationMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  // Initialise once. Cleanup on unmount avoids "already initialized" on HMR.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, {
      zoomControl: true,
      attributionControl: true,
      scrollWheelZoom: false, // don't hijack page scroll
    });
    L.tileLayer(config.tileUrl, {
      maxZoom: 19,
      attribution: config.tileAttribution,
    }).addTo(map);
    map.setView([54.5, -3], 5); // GB-ish default until points arrive
    mapRef.current = map;
    layerRef.current = L.layerGroup().addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Redraw whenever points, stops or mode change.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (!map || !layer) return;
    layer.clearLayers();
    const stopPins = stops ?? [];
    if (points.length === 0 && stopPins.length === 0) return;

    // Stop pins first, so driver markers draw on top. Solid = collection
    // (origin), hollow = delivery (destination) — the portal's map language.
    for (const s of stopPins) {
      L.circleMarker([s.lat, s.lng], {
        radius: 7,
        color: STOP_COLOR,
        weight: 2,
        fillColor: s.type === 'pickup' ? STOP_COLOR : '#ffffff',
        fillOpacity: 1,
      })
        .bindPopup(
          `<div style="font-size:13px;line-height:1.3"><div style="font-weight:600">${escapeHtml(
            s.label,
          )}</div><div style="color:${light.mute};font-size:12px">${
            s.type === 'pickup' ? 'Collection' : 'Delivery'
          }</div></div>`,
        )
        .addTo(layer);
    }

    const latLngs: [number, number][] = points.map((p) => [p.lat, p.lng]);
    const trail = mode === 'trail';

    // Trail line only joins one driver's path; markers mode leaves points loose.
    if (trail && latLngs.length > 1) {
      L.polyline(latLngs, { color: TRAIL_COLOR, weight: 2, opacity: 0.6 }).addTo(layer);
    }

    // Trail mode emphasises only the newest (last) fix; markers mode treats
    // every point as an equally-live current position.
    const lastIdx = points.length - 1;
    points.forEach((p, i) => {
      const emphasised = !trail || i === lastIdx;
      L.circleMarker([p.lat, p.lng], {
        radius: emphasised ? 7 : 3,
        color: emphasised ? LATEST_COLOR : TRAIL_COLOR,
        weight: emphasised ? 2 : 1,
        fillColor: emphasised ? LATEST_COLOR : TRAIL_COLOR,
        fillOpacity: emphasised ? 0.9 : 0.5,
      })
        .bindPopup(popupHtml(p, names)).addTo(layer);
    });

    // Fit over everything available — driver fixes AND stop pins. maxZoom
    // caps the single-point case at street level, not building level.
    const allLatLngs: [number, number][] = [
      ...latLngs,
      ...stopPins.map((s) => [s.lat, s.lng] as [number, number]),
    ];
    map.fitBounds(L.latLngBounds(allLatLngs), { padding: [24, 24], maxZoom: 15 });
  }, [points, mode, stops]);

  // isolation: 'isolate' gives the map its OWN stacking context: Leaflet's
  // internal panes/controls (z-index up to ~1000) can no longer escape and
  // beat the shell's overlays (nav drawer z-30, toasts z-50, dialogs are
  // top-layer). Fixed here in the shared component so every map is covered
  // — never per-page, never a z-index arms race.
  return (
    <div
      ref={containerRef}
      className={className}
      style={{ height: 288, isolation: 'isolate' }}
    />
  );
}
